import {
	Component,
	ComponentInterface,
	Element,
	Listen,
	Prop,
	Watch,
} from '@stencil/core';
import { KeyLinesOption } from '../../../../_storybook-helpers/cards';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';

const WindowResizeDebounceDelay = 200;
@Component({
	tag: 'ds-stat-card-row',
})
export class StatCardRow implements ComponentInterface {
	private keyLineTimer: number;

	@Element() el: HTMLDsStatCardRowElement;

	@Prop() keyLinesOption: KeyLinesOption; // The key lines option for this row.
	@Prop() nextRowKeyLinesOption: KeyLinesOption; // The key lines option for the next row. Affects styling of this row.
	@Prop() isLastRow: boolean; // If this is the last row in the stat card grid, then it affects the styling of the row.

	/**
	 * Listen to the window being resized and after a 'debounce' period, set the keylines.
	 */
	@Listen('resize', { target: 'window' })
	async onWindowResize(): Promise<void> {
		if (this.keyLineTimer) {
			window.clearTimeout(this.keyLineTimer);
		}
		this.keyLineTimer = window.setTimeout(
			() => this.setKeyLines(),
			WindowResizeDebounceDelay
		);
	}

	@Watch('keyLinesOption')
	onKeyLinesOptionChange() {
		this.setKeyLines();
	}

	@Watch('nextRowKeyLinesOption')
	onNextRowKeyLinesOptionChange() {
		this.setKeyLines();
	}

	@Watch('isLastRow')
	onIsLastRowChange() {
		this.setKeyLines();
	}

	/**
	 * Set which cards should show divider lines at the right or bottom (depending on breakpoint)
	 */
	setKeyLines() {
		const cards = Array.from(
			this.el.querySelectorAll('.ds-stat-card-container')
		);

		// If this row is not showing divider lines, there's nothing to do
		if (this.keyLinesOption !== 'divider') {
			return;
		}

		const totalCardsInRow = cards.length;

		cards.forEach((card, cardIndex) => {
			if (cardIndex + 1 == totalCardsInRow) {
				// This card is the last card in the row
				// If row is followed by another row with divider keyline option, its last card should have divider in XS-SM breakpoints
				if (
					!this.isLastRow &&
					this.nextRowKeyLinesOption === 'divider' &&
					[Breakpoint.XS, Breakpoint.SM].includes(getCurrentBreakpoint())
				) {
					card.classList.add('ds-divider');
				} else {
					card.classList.remove('ds-divider');
				}
			} else if (cardIndex + 1 === 2) {
				// This card is the second card in the row
				// If row contains 3 or more cards, 2nd card's keyline should be shown in all breakpoints apart from MD
				if (totalCardsInRow >= 3 && getCurrentBreakpoint() !== Breakpoint.MD) {
					card.classList.add('ds-divider');
				} else {
					// Never show for rows with 1 or 2 cards
					card.classList.remove('ds-divider');
				}
			} else {
				// For all other cards, just add the divider
				card.classList.add('ds-divider');
			}
		});
	}
}
